import React, { useEffect, useState, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
} from "reactstrap";
import { ToastContainer, toast } from "react-toastify";

import Breadcrumbs from "./Breadcrumbs";
import FAQHeader from "./FAQHeader";
import QuestionCard from "./QuestionCard";
import WaitingScreen from "./WaitingScreen";
import QuestionLocked from "./QuestionLocked";
import CompletedScreen from "./CompletedScreen";

import faqData from "../data/interactiveFaqData";

/**
 * Static/demo version of InteractiveFAQ.
 * No axios / BackendAPI / useAuth calls - everything is driven by
 * the local faqData array and a simple screen state machine:
 *
 *   question -> waiting -> question -> ... -> completed
 *
 * Screens:
 *   question  : current question is live, user can answer
 *   waiting   : answer submitted, showing "next question in X sec"
 *   locked    : kept for parity with the original component (unused by default flow)
 *   completed : all static questions have been answered
 */
const InteractiveFAQ = () => {
  const [registrationId] = useState("DEMO-0001");

  const [questionIndex, setQuestionIndex] = useState(0);

  const [selectedOption, setSelectedOption] = useState("");

  const [remainingTime, setRemainingTime] = useState(
    faqData[0]?.duration || 0
  );

  const [screen, setScreen] = useState("question");

  const timerRef = useRef(null);

  const currentQuestion = faqData[questionIndex] || null;

  /**
   * countdown - ticks every second while a screen has an active timer
   */
  useEffect(() => {
    if (screen === "completed") return undefined;

    timerRef.current = setInterval(() => {
      setRemainingTime((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(timerRef.current);
  }, [screen, questionIndex]);

  /**
   * timer finished handling
   */
  useEffect(() => {
    if (remainingTime !== 0) return;

    if (screen === "question") {
      toast.info("Question closed.");

      const gap = currentQuestion?.waitGap ?? 5;

      setScreen("waiting");

      setSelectedOption("");

      setRemainingTime(gap);

      return;
    }

    if (screen === "waiting") {
      goToNextQuestion();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remainingTime]);

  const goToNextQuestion = () => {
    const nextIndex = questionIndex + 1;

    if (nextIndex >= faqData.length) {
      setScreen("completed");
      return;
    }

    setQuestionIndex(nextIndex);

    setSelectedOption("");

    setRemainingTime(faqData[nextIndex].duration);

    setScreen("question");
  };

  /**
   * submit - static/local version, no API call
   */
  const handleSubmit = () => {
    if (!selectedOption) {
      toast.warning("Please select an option.");
      return;
    }

    console.log("Submitted answer (static):", {
      question_id: currentQuestion.id,
      option_id: selectedOption,
    });

    toast.success("Answer submitted successfully.");

    setSelectedOption("");

    setScreen("waiting");

    setRemainingTime(currentQuestion.waitGap ?? 5);
  };

  return (
    <>

      <Container fluid>
        <Row className="justify-content-center mt-4">
          <Col lg="8">
            <Card className="shadow border-0">
              <CardBody>
                <FAQHeader registrationId={registrationId} />

                {screen === "question" && currentQuestion && (
                  <QuestionCard
                    question={currentQuestion}
                    selectedOption={selectedOption}
                    setSelectedOption={setSelectedOption}
                    remainingTime={remainingTime}
                    onSubmit={handleSubmit}
                  />
                )}

                {screen === "waiting" && (
                  <WaitingScreen remainingTime={remainingTime} />
                )}

                {screen === "locked" && (
                  <QuestionLocked remainingTime={remainingTime} />
                )}

                {screen === "completed" && <CompletedScreen />}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <ToastContainer />
    </>
  );
};

export default InteractiveFAQ;
